import { ApprovalType, IEntityStage } from "../../../../../entities/Subentities";
import { ProjectInfo } from "../../../../../store/ProjectsListStore";

export type SkipToStageOptions = {
    isAdmin: boolean;
    hasStageStandardApprovals: boolean;
    hasStageGates: boolean;
};

export const SKIP_TO_STAGE_BLOCKED_TITLE = "The current stage requires approval or has incomplete required checklist items.";
export const SKIP_PAST_GATE_TITLE = "A previous stage requires approval or has incomplete required checklist items.";

export const isStandardOrGateDecisionApproval = (stage: IEntityStage, hasStageStandardApprovals: boolean, hasStageGates: boolean) =>
    (hasStageStandardApprovals && stage.attributes.ApprovalForTransition === ApprovalType.InApp)
    || (hasStageGates && stage.attributes.ApprovalForTransition === ApprovalType.GateDecision);

export const isSkipToStageBlocked = (project: ProjectInfo, options: SkipToStageOptions) => {
    const currentStage = project.stages[_getCurrentStageIndex(project)];
    return !options.isAdmin && !!currentStage && _isGatedStage(currentStage, options);
};

// A non-admin can land on a gated stage but not skip past one, matching the server-side check.
export const canSkipToStage = (project: ProjectInfo, targetStage: IEntityStage, options: SkipToStageOptions) => {
    if (options.isAdmin) {
        return true;
    }
    const currentStageIndex = _getCurrentStageIndex(project);
    const targetStageIndex = project.stages.findIndex(_ => _.externalId === targetStage.externalId);
    return !project.stages.some(
        (stage, index) => index >= currentStageIndex && index < targetStageIndex && _isGatedStage(stage, options)
    );
};

const _getCurrentStageIndex = (project: ProjectInfo) =>
    project.stages.findIndex(_ => _.externalId === project.attributes.Stage?.id);

const _isGatedStage = (stage: IEntityStage, options: SkipToStageOptions) =>
    _requiresApproval(stage, options) || _hasIncompleteRequiredChecklist(stage, options);

const _requiresApproval = (stage: IEntityStage, options: SkipToStageOptions) =>
    stage.attributes.ApprovalForTransition === ApprovalType.APIBased
    || isStandardOrGateDecisionApproval(stage, options.hasStageStandardApprovals, options.hasStageGates);

const _hasIncompleteRequiredChecklist = (stage: IEntityStage, options: SkipToStageOptions) =>
    options.hasStageGates
    && stage.attributes.StageChecklistEnabled
    && (stage.checklistItems ?? []).some(_ => _.isCompletionRequired && !_.isCompleted);
